import type { HTMLAttributes, ElementType } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/lib/utils";

const baseStyles = "tracking-[-0.02em] font-items";
const typography = cva(baseStyles, {
    variants: {
        variant: {
            h1: "text-[56px] md:text-[124px] leading-[1em] font-[400] tracking-[-1.5px]",
            h2: "text-[52px] md:text-[78px] leading-[48px] md:leading-[76px] tracking-[-1.4px]",
            h3: "text-[24px] md:text-[32px] leading-[26px] md:leading-[38px] font-items tracking-[-0.5px]",
            h4: "text-[24px] md:text-[28px] leading-[0.84em] font-medium",
            h5: "text-[22px] md:text-[24px] leading-[20px] md:leading-[26px] font-[400] md:tracking-[-0.5px]",
            body: "text-[16px] md:text-[18px] leading-[25px] md:leading-[1.6em] tracking-[0px] md:tracking-[-0.5px] font-[Helvetica] font-[400]",
            subtitle: "font-[Helvetica] font-[400] text-[14px] md:text-[22px] leading-[130%] md:leading-[140%] tracking-0",
            caption:
                "text-[14px] leading-[1.12em] font-medium tracking-[0.08em] font-platform font-[500]",
            description:
                "text-[14px] leading-[1.3em] tracking-[0em] font-medium font-[Helvetica] font-[400]",
            tiny: "text-[12px] leading-[1.12em] tracking-[0.08em] font-medium font-platform",
            tinyBody: "text-[14px] leading-[0.96em] font-medium",
            title: "text-[12px] leading-[15px] tracking-[1.3px] font-platform font-medium",
        },
        italic: {
            true: "italic",
        },
    },
    defaultVariants: {
        variant: "body",
        italic: false,
    },
});

type TypographyTag = keyof HTMLElementTagNameMap;

interface TypographyProps
    extends HTMLAttributes<HTMLElement>,
        VariantProps<typeof typography> {
    as?: TypographyTag;
}

export const Typography = ({
    as = "p",
    variant,
    italic,
    className,
    children,
    ...props
}: TypographyProps) => {
    const Component = as as ElementType;
    return (
        <Component
            className={cn(typography({ variant, italic }), className)}
            {...props}
        >
            {children}
        </Component>
    );
};
